import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Button, RadioGroup, TextInput } from "@aqar-app/aqar-design-system";
import { Select } from "@aqar-app/react-ui";
import { useMutation, useLazyQuery, useQuery } from "@apollo/client";
import { UPDATE_USER } from "../../graphql/mutations/auth";
import useForm from "../../hooks/useForm";
import FieldError from "../../components/field-error/FieldError";
import { useAuthState } from "../../contexts/auth/auth.provider";
import { setLoginCookies } from "../../lib/cookiesHelpers";
import { buildFileSrc, isValidEmail } from "../../lib/helpers";
import styles from "./userActions.module.scss";
import config from "@/common/config";
import { Modal } from "@aqar-app/react-ui";
import { ClassicModal } from "@/components/Listing/ListingScreen/classicModal";
import Link from "next/link";
import { trackAddEstablishmentPhotoFromSettings } from "@/lib/tracking/trackEstablishmentPhotoUpload";
import useIam from "@/hooks/useIam";
import { GET_ALLOWED_NAMES } from "../../graphql/queries/user";

const verifyUserInfoValidator = (values) => {
  const errors = {};
  const { name, email, chosen_user_type } = values;
  if (!name) {
    errors.name = "الرجاء إدخال اسم المستخدم.";
  }
  if (!chosen_user_type) {
    errors.chosen_user_type = "الرجاء إختيار نوع المستخدم.";
  }

  if (!email || !isValidEmail(email)) {
    errors.email = "الرجاء إدخال بريد الكتروني صحيح.";
  }
  return errors;
};

const USER_TYPE_OPTIONS = [
  { label: "باحث عن عقار", value: "seeker", className: styles.radioBtn },
  { label: "مالك", value: "owner", className: styles.radioBtn },
  { label: "مسوق", value: "marketer", className: styles.radioBtn },
  { label: "شركة", value: "company", className: styles.radioBtn },
];

const UserInfo = ({ handleSuccess, loginToken }) => {
  const [mutationError, setMutationError] = useState(null);
  const { token, user, updateContext } = useAuthState();
  const { iamStatus, checkingStatus } = useIam();
  const [nameOptions, setNameOptions] = useState([]);

  const isIamVerified = !!iamStatus?.id;

  const { data, loading: loadingNames } = useQuery(GET_ALLOWED_NAMES, {
    context: { token: token || loginToken },
    skip: !isIamVerified,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (data?.User?.getAllowedNames) {
      const names = data.User.getAllowedNames;
      setNameOptions(names.map((name) => ({ label: name, value: name })));
    }
  }, [data]);

  const getDefaultValues = () => {
    return {
      name: user?.name,
      email: user?.email,
      about: user?.about,
      chosen_user_type: user?.chosen_user_type,
    };
  };

  const [setUserData, { loading: updating }] = useMutation(UPDATE_USER, {
    context: { token: token || loginToken },
    fetchPolicy: "no-cache",
    onCompleted: ({ User }) => {
      const { update } = User;
      setLoginCookies(token || loginToken, update);
      updateContext({ token: token || loginToken, user: update });
      handleSuccess("تم تحديث الملف الشخصي بنجاح.");
    },
    onError: (err) => {
      setMutationError({ name: err.message });
    },
  });

  const { values, errors, handleChange, handleSubmit } = useForm(
    // eslint-disable-next-line no-use-before-define
    submit,
    verifyUserInfoValidator,
    getDefaultValues,
  );

  function submit() {
    setUserData({
      variables: { data: { ...values } },
    });
  }

  const isFieldInvalid = (field) => {
    return !!errors[field] || (mutationError && mutationError[field]);
  };

  const getFieldError = (field) => {
    return errors[field] || mutationError[field];
  };

  return (
    <div className={styles.authContainer}>
      <div className={styles.profileImageSection}>
        {user?.img ? (
          <img
            className={styles.userImage}
            alt={user?.name}
            src={buildFileSrc(`users/${user.img}`, config.IMAGES_CDN_BASE_URL)}
          />
        ) : (
          <>
            <svg
              width="70"
              height="70"
              viewBox="0 0 70 70"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect
                x="0.5"
                y="0.5"
                width="69"
                height="69"
                rx="5.5"
                fill="#F9FAFB"
                stroke="#E1E6EB"
              />
              <path
                d="M53.3769 46.7642C54.6558 48.6979 53.9433 51.2296 52.0517 52.5699C47.0908 56.085 41.1383 58 35 58C28.8536 58 22.8936 56.08 17.9288 52.556C16.0483 51.2214 15.3324 48.7071 16.5939 46.7768C17.571 45.2816 18.8447 43.987 20.3498 42.9807C22.9088 41.2699 25.9795 40.4975 29.0418 40.7944H40.9582C44.0176 40.5155 47.08 41.2951 49.6351 43.0033C51.1299 44.0027 52.3982 45.2845 53.3769 46.7642ZM45.5 22.4697C45.5 20.399 44.8869 18.3748 43.7384 16.653C42.5898 14.9313 40.9572 13.5894 39.0472 12.797C37.1372 12.0045 35.0354 11.7972 33.0078 12.2012C30.9801 12.6052 29.1175 13.6023 27.6557 15.0665C26.1938 16.5307 25.1983 18.3962 24.7949 20.4271C24.3916 22.4581 24.5986 24.5632 25.3898 26.4763C26.1809 28.3893 27.5207 30.0245 29.2397 31.1749C30.9587 32.3253 32.9796 32.9394 35.047 32.9394C36.4197 32.9394 37.779 32.6685 39.0472 32.1424C40.3154 31.6162 41.4677 30.8451 42.4384 29.8729C43.409 28.9007 44.179 27.7465 44.7043 26.4762C45.2296 25.206 45.5 23.8446 45.5 22.4697Z"
                fill="#999999"
              />
            </svg>
            <button
              className={styles.addImageBtn}
              onClick={() =>
                Modal.open(
                  <ClassicModal
                    newDesign
                    close={false}
                    cancel={false}
                    title="عقار"
                    ok={{
                      label: "حسنًا",
                      cb: (e) => {
                        Modal.close(e);
                      },
                    }}
                    body={<p>الخدمة متاحة فقط على تطبيق عقار للهواتف الذكية</p>}
                  />,
                )()
              }
            >
              اضغط لإضافة صورة شخصية
            </button>
          </>
        )}
        <Link
          href={
            user?.type === 1
              ? "/user/establishment-account"
              : "/user/verify-office"
          }
          className={styles.verifyAccount}
          onClick={() => {
            trackAddEstablishmentPhotoFromSettings();
          }}
        >
          لديك منشأة؟ اضغط هنا لإضافة الشعار
        </Link>
      </div>
      <div className={styles.label}>الاسم</div>
      <div>
        {checkingStatus ? (
          <TextInput
            name="name"
            placeholder="جاري التحميل..."
            value=""
            disabled
          />
        ) : isIamVerified ? (
          <Select
            value={values.name || ""}
            searchable={false}
            disabled={loadingNames}
            onSelect={(selected) =>
              handleChange(undefined, {
                name: selected || undefined,
              })
            }
          >
            {loadingNames ? (
              <Select.Option key="loading" disabled>
                <p>جاري التحميل...</p>
              </Select.Option>
            ) : (
              nameOptions.map((option) => (
                <Select.Option key={option.value} value={option.value}>
                  <p>{option.label}</p>
                </Select.Option>
              ))
            )}
          </Select>
        ) : (
          <TextInput
            name="name"
            placeholder="الاسم"
            value={values.name || ""}
            invalid={isFieldInvalid("name")}
            onChange={handleChange}
            onPressEnter={handleSubmit}
          />
        )}
        {isFieldInvalid("name") && <FieldError text={getFieldError("name")} />}
      </div>
      <div className={styles.label}>البريد الإلكتروني</div>
      <div>
        <TextInput
          name="email"
          value={values.email || ""}
          invalid={isFieldInvalid("email")}
          onChange={handleChange}
          onPressEnter={handleSubmit}
        />
        {isFieldInvalid("email") && (
          <FieldError text={getFieldError("email")} />
        )}
      </div>
      <div className={styles.label}>النبذة التعريفية</div>
      <div>
        <TextInput
          name="about"
          type="textarea"
          value={values.about || ""}
          invalid={isFieldInvalid("about")}
          onChange={handleChange}
          onPressEnter={handleSubmit}
        />
        {isFieldInvalid("about") && (
          <FieldError text={getFieldError("about")} />
        )}
      </div>
      <div className={styles.label}>نوع العضوية</div>
      <div>
        <RadioGroup
          key={values.chosen_user_type}
          className={styles.radioBtnWrapper}
          name="chosen_user_type"
          onChange={(evt, val) => {
            evt.target.value = val;
            handleChange(evt);
          }}
          defaultValue={values.chosen_user_type || ""}
          options={USER_TYPE_OPTIONS}
        />
        {isFieldInvalid("chosen_user_type") && (
          <FieldError text={getFieldError("chosen_user_type")} />
        )}
      </div>
      <Button
        className={styles.actionButton}
        contentClassName={styles.content}
        label="حفظ و استمرار"
        priority="primary"
        disabled={updating}
        onClick={handleSubmit}
      />
    </div>
  );
};

UserInfo.propTypes = {
  handleSuccess: PropTypes.func.isRequired,
  loginToken: PropTypes.string,
};

UserInfo.defaultProps = { loginToken: "" };

export default UserInfo;
